import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import { ChevronDown, Plus } from 'lucide-react';
import { Modal } from '../../../../../ui/modal/modal';

export type ModalOption = {
  label: string;
  modalContent?: JSX.Element | ((closeModal: () => void) => React.ReactNode);
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  value?: string;
  hoverStyle?: 'green' | 'red' | 'indigo';
};

type DropdownWithModalProps = {
  options: ModalOption[];
  disabled?: boolean;
  label?: string;
  className?: string;
  buttonColor?: 'green' | 'indigo' | 'gray';
  sx?: SxProps<Theme>;
};

export const DropdownWithModal: React.FC<DropdownWithModalProps> = ({
  options,
  disabled = false,
  label = 'Actions',
  buttonColor = 'indigo',
  sx,
}) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<ModalOption | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const resolveModalContent = (): JSX.Element | ((closeModal: () => void) => React.ReactNode) => {
    const foundOption = options.find((o) => o.value === selectedValue);

    if (foundOption?.modalContent) {
      return foundOption.modalContent;
    }
    return <Box />;
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOptionSelect = (option: ModalOption) => {
    if (!option.disabled) {
      if (option.modalContent) {
        setSelectedValue(option.value || '');
        setSelectedOption(option);
        setIsModalVisible(true);
      }
      if (option.onClick) {
        option.onClick();
      }
      handleCloseMenu();
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedValue('');
  };

  const getMuiColor = () => {
    switch (buttonColor) {
      case 'green':
        return 'success';
      case 'gray':
        return 'inherit';
      default:
        return 'primary';
    }
  };

  const isOpen = Boolean(anchorEl);

  return (
    <Box sx={[{ position: 'relative' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Button
        variant="outlined"
        color={getMuiColor() as any}
        disabled={disabled}
        onClick={handleOpenMenu}
        endIcon={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          >
            <ChevronDown size={18} />
          </Box>
        }
        sx={{
          minHeight: 40,
          px: { xs: 1.5, sm: 2 },
          py: 1,
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'none',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        {label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 200,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value || option.label}
            disabled={option.disabled}
            onClick={() => handleOptionSelect(option)}
            sx={{
              py: 1.25,
              px: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              {option.icon || (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Plus size={12} color="#2563eb" />
                </Box>
              )}
              <Typography variant="body2" fontWeight={500}>
                {option.label}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>

      {/* Modal */}
      {selectedOption?.modalContent && (
        <Modal
          visible={isModalVisible}
          toggle={closeModal}
          modalContent={resolveModalContent()}
        />
      )}
    </Box>
  );
};

export default DropdownWithModal;
